const { Type } = require('@sinclair/typebox');
const { actionUtils } = require('@sstdev/lib_metadata-config');
const Form = require('./Form');

/**
 * Transactions use the same schema extraction behavior as Form.
 * They are not normal edit forms, but when visible they still need read plus
 * create/update routes for their backing relation. The route-access hint lets
 * route/OpenAPI generation recover that without hardcoding Transaction there.
 * The generator requires an explicit asDataDictionary declaration.
 */
function asDataDictionary(hNode, context, parentAccessRestrictions) {
    // Transaction payload shape is derived from existing runtime contracts in:
    // - silo/ui/lib_ui-components/src/components/form/Transaction/index.js
    //   publishes selected transaction rows under "entries".
    // - silo/ui/lib_ui-core/src/rules/CRUD/namespace/relation/willCreate_namespace_relation_transaction.js
    //   builds payloads in the shape { title, entries, ...changes }.
    // - silo/ui/lib_ui-core/src/rules/CRUD/namespace/relation/doingCreate_namespace_relation_transaction.js
    //   resolves each entry by _id and falls back to a configurable title-alternative key.
    // - lib/metadata-transforms/lib_metadata-services/src/dataDictionaryService/relationProperties.js
    //   historically injected transaction title support and multipicker title-alternative behavior.
    // This transform keeps OpenAPI/AJV aligned with those existing contracts.
    function createTransactionTitleSchema() {
        return Type.Optional(
            Type.String({
                title: 'Transaction ID',
                'x-dataType': 'string',
                'x-propertyName': 'title',
                'x-optional': true,
                'x-editable': true
            })
        );
    }

    function createTransactionEntrySchema() {
        const entrySchema = Type.Object(
            {
                // _id is consumed by transaction apply logic to resolve records to update.
                _id: Type.Optional(
                    Type.String({
                        title: 'Record ID',
                        'x-dataType': 'string',
                        'x-propertyName': '_id',
                        'x-optional': true,
                        'x-editable': true
                    })
                ),
                // Keep title optional because runtime transaction apply logic can resolve by
                // title-alternative keys when _id is unavailable (for example multipicker rows).
                title: Type.Optional(
                    Type.String({
                        title: 'Title',
                        'x-dataType': 'string',
                        'x-propertyName': 'title',
                        'x-optional': true,
                        'x-editable': true
                    })
                )
            },
            {
                title: 'Transaction Entry',
                'x-dataType': 'object',
                additionalProperties: true
            }
        );

        return Type.Optional(
            Type.Array(entrySchema, {
                title: 'Entries',
                'x-dataType': 'array',
                'x-propertyName': 'entries',
                'x-optional': true,
                'x-editable': true
            })
        );
    }

    function withTransactionPayloadSemantics(schema) {
        if (!schema || typeof schema !== 'object') return schema;

        if (Array.isArray(schema.anyOf)) {
            return {
                ...schema,
                anyOf: schema.anyOf.map(branch => withTransactionPayloadSemantics(branch))
            };
        }

        const properties = { ...(schema.properties || {}) };
        if (!properties.title) properties.title = createTransactionTitleSchema();
        if (!properties.entries) properties.entries = createTransactionEntrySchema();

        return {
            ...schema,
            type: schema.type || 'object',
            properties,
            // Transaction payloads combine selected entries with dynamic change keys from blocks
            // like DynamicFieldEntry. Permit extra keys only for transactions so standard Form
            // schemas remain strict with additionalProperties: false.
            additionalProperties: true
        };
    }

    function applyTransactionSchemaShape(dataDictionary, transactionHNode) {
        const { namespace, relation } = transactionHNode || {};
        // Scope permissive payload semantics to transaction endpoints only.
        if (!namespace || !relation || !relation.endsWith('-transaction')) return;
        const relationSchema = dataDictionary?.[namespace]?.[relation];
        if (!relationSchema) return;
        dataDictionary[namespace][relation] = withTransactionPayloadSemantics(relationSchema);
    }

    const normalizedAction = actionUtils.normalizeAction(hNode.forAction);
    // The transaction form is the UI proof that these routes are needed.
    // Leave that proof in the schema so routeAccessService does not need to know
    // what a Transaction block is.
    const routeAccessHints = {
        ...(hNode.routeAccessHints || {}),
        get: hNode.routeAccessHints?.get ?? true,
        ...(normalizedAction === 'create' ? { post: hNode.routeAccessHints?.post ?? true } : {}),
        ...(normalizedAction === 'update' ? { patch: hNode.routeAccessHints?.patch ?? true } : {})
    };

    const [subDataDictionary = {}, partialTypeDefinition = {}] = Form.asDataDictionary(
        {
            ...hNode,
            routeAccessHints
        },
        context,
        parentAccessRestrictions
    );

    applyTransactionSchemaShape(subDataDictionary, hNode);
    return [subDataDictionary, partialTypeDefinition];
}

module.exports = { asDataDictionary };
